import { useEffect, useState } from 'react';
import SectionAccordion from '_components/UI/SectionAccordion';
import ProductRatingStars from '_components/Product/ProductRatingStars';
import { useTranslation } from 'next-i18next';
import trackBazaarvoiceEvent from '@utils/gtm/ga4/trackBazaarvoiceEvents';
import styles from './ProductReviews.module.scss';

interface ProductReviewsProps {
  productCode: string;
  productReviewData: {
    numberOfReviews: number;
    summationOfRatings: number;
  };
  reviewsAccordionOpen: boolean;
}

const ProductReviews = ({
  productCode,
  productReviewData,
  reviewsAccordionOpen,
}: ProductReviewsProps) => {
  const { t } = useTranslation('pdp');
  const [isBazaarvoiceReady, setIsBazaarvoiceReady] = useState(true);

  useEffect(() => {
    const bazaarvoiceDiv = document.getElementById('reviews');
    if (!bazaarvoiceDiv) return;

    setIsBazaarvoiceReady(true);

    const checkReady = () => {
      const isReady = bazaarvoiceDiv.getAttribute('data-bv-ready') === 'true';
      if (isReady) setIsBazaarvoiceReady(false);
      return isReady;
    };

    if (checkReady()) return;

    const observer = new MutationObserver(() => {
      if (checkReady()) observer.disconnect();
    });

    observer.observe(bazaarvoiceDiv, {
      attributes: true,
      attributeFilter: ['data-bv-ready'],
    });

    return () => observer.disconnect();
  }, [productCode]);

  return (
    <SectionAccordion
      icon="plus"
      className={styles.productReviewsSection}
      disabled={isBazaarvoiceReady}
      isOpen={reviewsAccordionOpen}
      onStateChange={(state) => {
        if (state === 'open') trackBazaarvoiceEvent('Reviews');
      }}
    >
      <SectionAccordion.Header
        className={styles.productReviewsHeader}
        title={
          <span className={styles.productReviewsTitle}>
            {t('product.reviews')}
          </span>
        }
        subtitle={
          <ProductRatingStars
            className={styles.productReviewsSubtitle}
            rating={productReviewData.summationOfRatings}
            reviewCount={productReviewData.numberOfReviews}
            showNumericRating
            showReviewCount
            starSize="Large"
          />
        }
      />
      <SectionAccordion.Collapsible>
        <div
          id="reviews"
          data-bv-show="reviews"
          data-bv-product-id={productCode}
          data-testid="reviews"
        />
      </SectionAccordion.Collapsible>
    </SectionAccordion>
  );
};

export default ProductReviews;
